import { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { dispatch } from 'store'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { projectDataThunks } from 'store/features/projectData/thunks'
import {
  IMediaFile,
  projectDataMediaThunks
} from 'store/features/projectData/thunks/media'
import { handleApiError } from 'store/utils'
import { ErrorKeys } from 'consts/errorKeys'
import { videoTypes } from 'consts'
import { IMediaItem, MediaStatus } from 'types/media'
import { extractFrameAtTimestamp } from 'services/video-thumbs/utils'
import { scrollToElementById } from 'utils'
import { calculateTotalMediaDuration } from 'utils/media'
import { useTimelineContext } from 'context/TimelineContext'
import useDurationLimitCheck from 'hooks/useDurationLimitCheck'
import { useGetOverlappingVideoItem } from './useGetOverlappingVideoItem'
import { getPlaceholderMediaItem, getTimestampWithinMediaItem } from './utils'
import { FileRegistry } from 'utils/file-registry/FileRegistry'

const fileRegistry = FileRegistry.getInstance()

export const useFrameExtraction = () => {
  const canvasDefaultDimensions = useSelector(
    projectDataSelectors.defaultDimensions
  )
  const [isUploading, setIsUploading] = useState(false)
  const { getCurrentTimeImmediate, isPlaying } = useTimelineContext()
  const currentSelectedItem = useSelector(projectDataSelectors.activeMedia)
  const { isDurationAllowed } = useDurationLimitCheck()
  const { overlappingVideoItem } = useGetOverlappingVideoItem()

  const selectedItemValid = useMemo(() => {
    return (
      (currentSelectedItem && videoTypes.includes(currentSelectedItem.type)) ??
      false // if no selected item, we can extract frame from the overlapping video item handled by the hook
    )
  }, [currentSelectedItem])

  const isButtonDisabled =
    !selectedItemValid || !overlappingVideoItem || isPlaying || isUploading

  const uploadFrame = useCallback(
    async (
      frameBlob: Blob,
      frameBlobUrl: string,
      placeholderItem: IMediaItem
    ) => {
      try {
        setIsUploading(true)
        const frameFileName = `frame-${placeholderItem.id}.jpg`
        const frameFile = new File([frameBlob], frameFileName, {
          type: 'image/jpeg'
        })

        const uploadedMedias = (await dispatch(
          projectDataThunks.uploadMedias([
            {
              id: placeholderItem.id,
              type: 'image',
              file: frameFile
            }
          ])
        )) as IMediaFile[]

        if (!uploadedMedias[0]) {
          throw new Error('Failed to upload frame')
        }

        dispatch(
          projectDataThunks.updateMediaItemWithoutUndoSave({
            id: placeholderItem.id,
            updates: {
              meta: {
                ...placeholderItem.meta,
                path: uploadedMedias[0].filePath,
                originalWidth: uploadedMedias[0].width,
                originalHeight: uploadedMedias[0].height,
                status: MediaStatus.OK
              }
            }
          })
        )
      } catch {
        dispatch(handleApiError(ErrorKeys.GENERAL_API_ERROR))
        dispatch(projectDataThunks.removeVisualMediaItem(placeholderItem.id))
      } finally {
        fileRegistry.deleteFile(frameBlobUrl)
        setIsUploading(false)
      }
    },
    []
  )

  const handleExtractFrame = useCallback(async () => {
    const videoPath = overlappingVideoItem?.meta?.path
    if (
      !overlappingVideoItem ||
      !videoTypes.includes(overlappingVideoItem.type) ||
      !videoPath
    ) {
      return
    }

    const currentTimelineTime = getCurrentTimeImmediate()
    const clampedTimestamp = getTimestampWithinMediaItem(
      overlappingVideoItem,
      currentTimelineTime
    )

    const extractedFrameStartTime =
      overlappingVideoItem.startTime + overlappingVideoItem.duration

    // Extract the frame BEFORE adding the placeholder to the timeline so that
    // meta.path is never null when the engine picks up the item (otherwise the
    // engine tries to load an image from a null src and throws).
    let frameBlob: Blob
    try {
      frameBlob = await extractFrameAtTimestamp(videoPath, clampedTimestamp)
    } catch {
      dispatch(handleApiError(ErrorKeys.GENERAL_API_ERROR))
      return
    }
    const frameBlobUrl = fileRegistry.registerFile(frameBlob)

    const placeholderItem = getPlaceholderMediaItem(
      extractedFrameStartTime,
      canvasDefaultDimensions,
      overlappingVideoItem,
      frameBlobUrl
    )

    const totalNewDuration = calculateTotalMediaDuration([placeholderItem])
    if (!isDurationAllowed(totalNewDuration)) {
      fileRegistry.deleteFile(frameBlobUrl)
      return
    }

    dispatch(
      projectDataMediaThunks.adjustTimelineForNewItems(
        [placeholderItem],
        overlappingVideoItem.id
      )
    )
    scrollToElementById(placeholderItem.id)

    await uploadFrame(frameBlob, frameBlobUrl, placeholderItem)
  }, [
    overlappingVideoItem,
    getCurrentTimeImmediate,
    canvasDefaultDimensions,
    isDurationAllowed,
    uploadFrame
  ])

  return {
    handleExtractFrame,
    isButtonDisabled
  }
}
